import React from "react";
import { errorMessage } from "@atlas/shared";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Compass, Check } from "lucide-react";
import { fetchPartnerCode, claimPartnerCode, storePartnerCode } from "../api/partners";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";

// Partner landing (ATL-134): /with/{code}, and /beta/{code} for links already
// out there. Fetching the code also sets the HttpOnly attribution cookie, so
// a Strava-first signup from here is still attributed at the OAuth callback.

const CARD_STYLE: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-xl)",
  padding: "var(--space-8)",
  boxShadow: "var(--shadow-md), var(--ring-top)",
};

const PERKS = [
  "Every walk you've already done, matched against peaks, trails and landmarks",
  "Collections, regions and achievements that fill themselves in",
  "Nothing to record: connect a source or drop in a GPX",
];

function trialLabel(days: number): string {
  if (days === 365) return "a year";
  if (days === 90) return "3 months";
  if (days === 30) return "a month";
  return `${days} days`;
}

function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function GoldLinkButton({ to, search, children }: {
  to: string;
  search?: Record<string, string>;
  children: string;
}) {
  return (
    <Link
      to={to}
      search={search}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "var(--control-lg)",
        background: "var(--accent)",
        color: "var(--text-on-gold)",
        borderRadius: "var(--radius-md)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-base)",
        fontWeight: 600,
        textDecoration: "none",
      }}
    >
      {children}
    </Link>
  );
}

export default function PartnerLandingPage() {
  const { code } = useParams({ strict: false }) as { code: string };
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  usePageMeta({
    title: "You're invited to Atlas",
    description: "Keep your time outside on the record.",
    canonicalPath: `/with/${code}`,
    noindex: true,
  });

  const partnerQuery = useQuery({
    queryKey: ["partner", code],
    queryFn: () => fetchPartnerCode(code),
    retry: false,
  });

  React.useEffect(() => {
    if (partnerQuery.data) storePartnerCode(partnerQuery.data.partner.code);
  }, [partnerQuery.data]);

  const claimMutation = useMutation({
    mutationFn: () => claimPartnerCode(code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["me"] });
      void qc.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  if (partnerQuery.isLoading || authLoading) {
    return (
      <div style={CARD_STYLE}>
        <p style={{ fontSize: 14, color: "var(--text-muted)" }}>Checking your link…</p>
      </div>
    );
  }

  if (partnerQuery.isError || !partnerQuery.data) {
    return (
      <div style={CARD_STYLE}>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", marginBottom: 10 }}>
          This link isn't valid
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20 }}>
          It may have been switched off, or the link was copied incorrectly. You can
          still create a free account.
        </p>
        <GoldLinkButton to="/signup">Create a free account</GoldLinkButton>
      </div>
    );
  }

  const partner = partnerQuery.data.partner;
  const sender = partner.label;
  const trialOpen = partner.proDays != null && !partner.full;
  const eyebrow = sender ? `${sender} sent you` : "You've been sent here";

  if (claimMutation.isSuccess) {
    const expires = claimMutation.data.expiresAt
      ? new Date(claimMutation.data.expiresAt).toLocaleDateString()
      : null;
    return (
      <div style={CARD_STYLE}>
        <div className="eyebrow" style={{ marginBottom: "var(--space-2)" }}>You're in</div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", marginBottom: 10 }}>
          {claimMutation.data.trial ? "Pro trial activated" : "Link applied"}
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20 }}>
          {claimMutation.data.trial
            ? `You now have Atlas Pro${expires ? ` until ${expires}` : ""}. Enjoy the full map.`
            : claimMutation.data.trialRefused === "full"
              ? "Thanks for coming in this way. The Pro trials on this link have all been taken."
              : claimMutation.data.trialRefused === "already_pro"
                ? "Thanks for coming in this way. You already have Pro, so there's no trial to add."
                : claimMutation.data.trialRefused === "already_member"
                  ? "Thanks for coming in this way. You've already had a partner Pro trial, so there isn't another."
                  : "Thanks for coming in this way. Nothing else changes on your account."}
        </p>
        <GoldLinkButton to="/dashboard">Go to your dashboard</GoldLinkButton>
      </div>
    );
  }

  return (
    <div style={CARD_STYLE}>
      <div style={{ marginBottom: "var(--space-6)" }}>
        <div className="eyebrow" style={{ marginBottom: "var(--space-2)", display: "flex", alignItems: "center", gap: 6 }}>
          <Compass size={12} />
          {eyebrow}
        </div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>
          {trialOpen
            ? `${trialLabel(partner.proDays!)} of Atlas Pro, on ${sender ?? "us"}`
            : "The record of your time outside"}
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 8 }}>
          {partner.landingLine ??
            "Atlas turns the walks you've already done into collections, regions and achievements."}
        </p>
        {partner.proDays != null && partner.full && (
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 8 }}>
            All the Pro trials on this link have been taken, but a free account still
            matches your last twelve months.
          </p>
        )}
        {partner.rewardWaypoints != null && partner.rewardWaypoints > 0 && (
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 8 }}>
            {trialOpen ? "Plus" : "You also get"} {partner.rewardWaypoints} Waypoints to spend in the Atlas store once your
            first walk syncs.
          </p>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "var(--space-6)" }}>
        {PERKS.map((perk) => (
          <div key={perk} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
            <Check size={13} style={{ color: "var(--text-accent)", flexShrink: 0 }} />
            {perk}
          </div>
        ))}
      </div>

      {user ? (
        <>
          <button
            onClick={() => claimMutation.mutate()}
            disabled={claimMutation.isPending}
            style={{
              width: "100%",
              height: "var(--control-lg)",
              background: claimMutation.isPending ? "var(--accent-press)" : "var(--accent)",
              color: "var(--text-on-gold)",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-base)",
              fontWeight: 600,
              cursor: claimMutation.isPending ? "not-allowed" : "pointer",
            }}
          >
            {claimMutation.isPending ? "Applying…" : trialOpen ? "Claim your Pro trial" : "Apply this link"}
          </button>
          {claimMutation.isError && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 12 }}>
              {errorMessage(claimMutation.error, "Couldn't apply this link")}
            </p>
          )}
          <p style={{ marginTop: "var(--space-4)", fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
            Signed in as {user.email}.{" "}
            <button
              onClick={() => void navigate({ to: "/dashboard" })}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--text-accent)", fontSize: 12, fontWeight: 600 }}
            >
              Skip to dashboard
            </button>
          </p>
        </>
      ) : (
        <>
          <GoldLinkButton to="/signup" search={{ invite: partner.code }}>
            Create your account
          </GoldLinkButton>
          <p style={{ marginTop: "var(--space-4)", fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "var(--text-accent)", fontWeight: 600 }}>
              Sign in
            </Link>{" "}
            then reopen this link.
          </p>
          {isMobileBrowser() && (
            // Signing up here is the safe order: the code sticks, and the
            // next page points them at the app. Someone already holding the
            // app can type the code there instead (the store can't carry it).
            <p style={{ marginTop: "var(--space-3)", fontSize: 12, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.5 }}>
              Already have the app?{" "}
              <a href={`atlas://with/${encodeURIComponent(partner.code)}`} style={{ color: "var(--text-accent)", fontWeight: 600 }}>
                Open it here
              </a>
              , or enter code{" "}
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)", fontWeight: 600 }}>
                {partner.code.toUpperCase()}
              </span>{" "}
              when you sign up.
            </p>
          )}
        </>
      )}
    </div>
  );
}
